import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/api";

function NewVisit() {
  const { id } = useParams(); // patient id
  const navigate = useNavigate();

  const now = new Date();
  const [visitDate, setVisitDate] = useState(
    now.toISOString().split("T")[0]
  );
  const [visitTime, setVisitTime] = useState(
    now.toTimeString().slice(0, 5)
  );

  // MODE: "images" or "text"
  const [mode, setMode] = useState("images");

  // Text mode
  const [notes, setNotes] = useState("");

  // Image mode
  const [images, setImages] = useState([]);

  const [saving, setSaving] = useState(false);

  /* ---------------- IMAGE HANDLERS ---------------- */

  const handleImageSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setImages((prev) => [...prev, ...selectedFiles]);
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  /* ---------------- SAVE VISIT ---------------- */

  const handleSaveVisit = async () => {
    try {
      setSaving(true);

      // 1️⃣ Create visit
      const res = await api.post(`/api/visits/${id}`, {
        visit_date: visitDate,
        visit_time: visitTime,
        notes: mode === "text" ? notes : null,
      });

      const visitId = res.data.visit_id;

      // 2️⃣ Upload images if image mode
      if (mode === "images" && images.length > 0) {
        const formData = new FormData();
        images.forEach((img) => formData.append("images", img));

        await api.post(
          `/api/visit-images/${visitId}/prescription`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }

      navigate(`/patients/${id}`);
    } catch (err) {
      console.error("Failed to save visit", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-4 pb-20">
      <button
        onClick={() => navigate(-1)}
        className="text-blue-600 mb-4"
      >
        ← Back
      </button>

      <h1 className="text-xl font-semibold mb-4">
        Add Visit
      </h1>

      {/* Date & Time */}
      <input
        type="date"
        className="w-full h-11 px-3 mb-3 border rounded-lg"
        value={visitDate}
        onChange={(e) => setVisitDate(e.target.value)}
      />

      <input
        type="time"
        className="w-full h-11 px-3 mb-4 border rounded-lg"
        value={visitTime}
        onChange={(e) => setVisitTime(e.target.value)}
      />

      {/* Mode Toggle */}
      <div className="flex mb-4 gap-2">
        <button
          onClick={() => setMode("images")}
          className={`flex-1 h-11 rounded-lg ${
            mode === "images"
              ? "bg-blue-600 text-white"
              : "bg-gray-200"
          }`}
        >
          📷 Images
        </button>

        <button
          onClick={() => setMode("text")}
          className={`flex-1 h-11 rounded-lg ${
            mode === "text"
              ? "bg-blue-600 text-white"
              : "bg-gray-200"
          }`}
        >
          ✍️ Text
        </button>
      </div>

      {/* IMAGE MODE */}
      {mode === "images" && (
        <>
          <input
            type="file"
            multiple
            accept="image/*"
            className="w-full mb-3"
            onChange={handleImageSelect}
          />

          {/* IMAGE PREVIEWS */}
          {images.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-4">
              {images.map((img, index) => (
                <div key={index} className="relative">
                  <img
                    src={URL.createObjectURL(img)}
                    alt="preview"
                    className="w-full h-24 object-cover rounded-lg border"
                  />

                  <button
                    onClick={() => removeImage(index)}
                    className="
                      absolute
                      top-1
                      right-1
                      w-6
                      h-6
                      bg-red-600
                      text-white
                      rounded-full
                      text-xs
                      flex
                      items-center
                      justify-center
                    "
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* TEXT MODE */}
      {mode === "text" && (
        <textarea
          rows={4}
          className="w-full p-3 mb-4 border rounded-lg"
          placeholder="Write notes or prescription"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      )}

      {/* SAVE */}
      <button
        onClick={handleSaveVisit}
        disabled={saving}
        className="
          w-full
          h-12
          bg-green-600
          text-white
          rounded-xl
          font-medium
          active:bg-green-700
        "
      >
        {saving ? "Saving..." : "Save Visit"}
      </button>
    </div>
  );
}

export default NewVisit;
